import Booking from '../types/model/Booking';
import service from '../services/RestService';
import AbstractRestRepository from './AbstractRestRepository';
import { BookingStatus, BookingStatusName } from '../enums/BookingStatus';
import { ErrorData } from '../types/response/RestErrorResponse';
import { RestResponse } from '../types/response/RestResponse';
import { CancellationReason, CancellationReasonName } from '../enums/CancellationReason';

type BookingStatusRequest = {
  status?: string;
  bookingDoneAt?: string;
  reason?: string;
  reasonText?: string;
};

export default class BookingRepository extends AbstractRestRepository<Booking> {
  async getAll(params: { projectId: number; startDate?: string; endDate?: string }): Promise<Booking[]> {
    const response = await service.get(`/booking/by-project-id/${params.projectId}?startDate=${params.startDate}&endDate=${params.endDate}`);
    return super.unpackResponseContent(response) ?? [];
  }

  async create(booking: Partial<Booking>): Promise<Booking> {
    const response = await service.post(`/booking/project/${booking.projectId}`, booking);
    return super.unpackResponseContent(response);
  }

  async updateStatus(
    booking: Booking,
    status: BookingStatus,
    reason?: CancellationReason,
    reasonText?: string,
    isRejected?: boolean,
    bookingDoneAt?: string
  ): Promise<Booking> {
    const payload: BookingStatusRequest = {};
    let response: RestResponse<ErrorData>;

    if (status === BookingStatus.Canceled) {
      if (booking.confirmationStatus === BookingStatus.Open && (!reason || !reasonText)) {
        throw new Error('Reason is required for cancelling a booking');
      }
      payload.reason = CancellationReasonName[reason as CancellationReason];
      payload.reasonText = reasonText;

      response = (await service.post(`/booking/${isRejected ? 'reject' : 'cancel'}/${booking.id}`, payload)) as RestResponse<ErrorData>;
    } else {
      payload.status = BookingStatusName[status];
      if (bookingDoneAt) {
        payload.bookingDoneAt = bookingDoneAt;
      }

      response = await service.put(`/booking/${booking.id}/status`, payload);
    }
    if (!response.data.result) {
      throw response;
    }
    const bookingUpdated = await (
      await this.getAll({ projectId: booking.projectId, startDate: booking.date, endDate: booking.date })
    ).find((f) => f.id === booking.id);
    return {
      ...bookingUpdated,
      confirmationStatus: status
    } as Booking;
  }
}
